// ReceiptKit — the shared newspaper-receipt primitives, so every surface speaks the
// landing's design language without copy-paste. The CSS for these classes
// (.wt-receipt-page, .wt-receipt-grain, .wt-receipt-wordmark, .wt-eyebrow, .wt-ticks,
// .wt-receipt-rule) is globally bundled from the landing sheet. Square / flat / one
// amber — enforced by scripts/design-gate.mjs.
import type { ReactNode } from "react";

/** The washed-paper substrate + newsprint grain overlay (responds to the theme via
 * the [data-appearance] scoping). Wrap any full-page receipt surface in this. */
export function ReceiptPage({
  className = "",
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={["wt-receipt-page", className].filter(Boolean).join(" ")}>
      <div className="wt-receipt-grain" aria-hidden />
      {children}
    </div>
  );
}

/** The WAYTIDE masthead wordmark with the ™ industrial mark. */
export function Wordmark() {
  return (
    <span className="wt-receipt-wordmark">
      WAYTIDE
      <span className="wt-receipt-wordmark__mark" aria-hidden>
        ™
      </span>
    </span>
  );
}

/** A tactical eyebrow — an ASCII-bracketed mono label `[ LABEL ]`. */
export function Eyebrow({ label }: { label: string }) {
  return (
    <span className="wt-eyebrow wt-eyebrow--solo">
      <span className="wt-eyebrow__tag">
        <span className="wt-eyebrow__bracket" aria-hidden>
          [
        </span>
        <span className="wt-eyebrow__label">{label}</span>
        <span className="wt-eyebrow__bracket" aria-hidden>
          ]
        </span>
      </span>
    </span>
  );
}

/** A dashed coupon tear-rule — one thick butt-capped dashed line on the receipt
 * dash cadence (the landing's scissor cut-line). */
export function TearRule() {
  return (
    <svg
      className="wt-receipt-rule"
      width="100%"
      height={8}
      preserveAspectRatio="none"
      aria-hidden
      focusable="false"
    >
      <line
        x1="0"
        y1={4}
        x2="100%"
        y2={4}
        stroke="var(--waytide-comp-receipt-rule)"
        strokeDasharray="var(--waytide-comp-receipt-dash)"
        strokeWidth="var(--waytide-comp-receipt-rule-width)"
        strokeLinecap="butt"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/** Four corner registration ticks framing a focal card (the blueprint texture). */
export function CornerTicks() {
  return (
    <span className="wt-ticks" aria-hidden>
      <span className="wt-ticks__c wt-ticks__c--tl" />
      <span className="wt-ticks__c wt-ticks__c--tr" />
      <span className="wt-ticks__c wt-ticks__c--bl" />
      <span className="wt-ticks__c wt-ticks__c--br" />
    </span>
  );
}
